/* transport-wizard.js - 6-step quote-based checkout (sectie 5.5 pattern)
 * - Step-state + validation + photo-upload (5×5MB max)
 * - Client-side image compress (kleinste dimensie 1600px)
 * - GA4 events: begin_checkout, wizard_step_completed, wizard_photo_uploaded, generate_lead
 *
 * VERZENDING, herzien 8-8-2026. Hiervoor ging de aanvraag uitsluitend naar een
 * Trigger.dev-webhook waarvan de URL uit <meta name="trigger-webhook-url"> komt. Die
 * themasetting is NOOIT gevuld, dus op de live site viel de code in de else-tak: de
 * aanvraag werd naar de browserconsole gelogd, de bezoeker kreeg "gelukt" te zien en er
 * ging een generate_lead van EUR 50 naar Meta. Elke aanvraag verdween dus stil, terwijl
 * de conversie wel geteld werd. Dat is nu omgedraaid:
 *   1. de tekstvelden gaan ALTIJD naar Shopify's eigen contactformulier (POST /contact),
 *      dus ze komen aan op het adres uit de winkelinstellingen;
 *   2. is er wel een webhook ingesteld, dan gaat de volledige payload met foto's daar
 *      ook naartoe, als extra en niet als enige route;
 *   3. generate_lead en het succesbericht komen alleen nog als stap 1 is gelukt.
 * Shopify's contactformulier accepteert geen bestanden, dus zonder webhook gaan de foto's
 * niet mee. Daarom staat in de aanvraagmail hoeveel foto's er waren, met het verzoek ze
 * na te sturen.
 */
(() => {
  'use strict';
  const root = document.querySelector('.transport-wizard');
  if (!root) return;

  const form = root.querySelector('[data-wizard-form]');
  const TOTAL_STEPS = 6;
  const MAX_PHOTOS = 5;
  const MAX_PHOTO_BYTES = 5 * 1024 * 1024;

  let currentStep = 1;
  const uploadedPhotos = [];

  const $$ = (sel, ctx = root) => ctx.querySelectorAll(sel);
  const $ = (sel, ctx = root) => ctx.querySelector(sel);

  // ---- Transportkeuze: bezorgen door ons of zelf ophalen in Tilburg ----
  // Bij zelf ophalen zijn bereikbaarheid (3) en foto's (4) niet relevant.
  const SKIP_WHEN_PICKUP = [3, 4];
  const ADDRESS_FIELDS = ['street', 'postcode', 'city', 'province'];
  function isPickup() {
    const el = $('[data-transport-choice="zelf"]');
    return !!(el && el.checked);
  }
  function applyTransportChoice() {
    const pickup = isPickup();
    const addr = $('[data-address-fields]');
    const note = $('[data-pickup-note]');
    if (addr) {
      addr.classList.toggle('hidden', pickup);
      addr.querySelectorAll('input').forEach(i => {
        if (ADDRESS_FIELDS.includes(i.name)) i.required = !pickup;
      });
    }
    if (note) note.classList.toggle('hidden', !pickup);
    SKIP_WHEN_PICKUP.forEach(s => {
      const li = root.querySelector(`[data-wizard-progress] [data-step="${s}"]`);
      if (li) li.style.opacity = pickup ? '0.3' : '';
    });
  }
  function stepAfter(n, dir) {
    let s = n + dir;
    while (isPickup() && SKIP_WHEN_PICKUP.includes(s)) s += dir;
    return Math.max(1, Math.min(TOTAL_STEPS, s));
  }
  $$('[data-transport-choice]').forEach(i => i.addEventListener('change', applyTransportChoice));

  // ---- Step nav ----
  function showStep(n) {
    currentStep = n;
    const csEl = $('[data-current-step]'); if (csEl) csEl.textContent = n;
    $$('.wizard-step').forEach(el => el.classList.toggle('hidden', el.dataset.step != n));
    $$('[data-wizard-progress] [data-step]').forEach(el => {
      const dot = el.querySelector('.step-dot');
      const isActive = el.dataset.step == n;
      const isDone = el.dataset.step < n;
      dot.className = 'step-dot w-7 h-7 rounded-full flex items-center justify-center font-bold ' +
        (isActive ? 'bg-[#D4A017] text-[#0A2540]' : isDone ? 'bg-[#0A2540] text-[#D4A017]' : 'bg-[#0A2540]/10 text-[#6E6457]');
    });
    $('[data-wizard-prev]').classList.toggle('hidden', n === 1);
    $('[data-wizard-next]').classList.toggle('hidden', n === TOTAL_STEPS);
    $('[data-wizard-submit]').classList.toggle('hidden', n !== TOTAL_STEPS);

    if (n === TOTAL_STEPS) renderReview();
    if (window.trackEvent) window.trackEvent('wizard_step_completed', { step_number: n });
  }

  function validateStep(n) {
    const fieldset = $(`.wizard-step[data-step="${n}"]`);
    if (!fieldset) return true;
    const requiredInputs = fieldset.querySelectorAll('input[required], select[required]');
    for (const input of requiredInputs) {
      if (!input.value.trim()) {
        input.focus();
        input.classList.add('border-[#C26A4E]');
        return false;
      }
      input.classList.remove('border-[#C26A4E]');
    }
    return true;
  }

  // ---- Photo upload (client-side compress + size-check) ----
  const photoInput = $('[data-photo-input]');
  const photoPreview = $('[data-photo-preview]');
  const photoStatus = $('[data-photo-status]');

  function compressImage(file) {
    return new Promise(resolve => {
      const img = new Image();
      img.onload = () => {
        const max = 1600;
        let { width, height } = img;
        if (width > max || height > max) {
          if (width > height) { height = height * max / width; width = max; }
          else { width = width * max / height; height = max; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        canvas.toBlob(blob => resolve(new File([blob], file.name, { type: 'image/jpeg' })), 'image/jpeg', 0.85);
      };
      img.src = URL.createObjectURL(file);
    });
  }

  photoInput?.addEventListener('change', async (e) => {
    const files = Array.from(e.target.files).slice(0, MAX_PHOTOS - uploadedPhotos.length);
    for (const f of files) {
      if (f.size > MAX_PHOTO_BYTES * 2) {
        photoStatus.textContent = `⚠ ${f.name} is groter dan 10MB en wordt overgeslagen.`;
        continue;
      }
      const compressed = await compressImage(f);
      if (compressed.size > MAX_PHOTO_BYTES) {
        photoStatus.textContent = `⚠ ${f.name} kan niet onder 5MB worden gebracht.`;
        continue;
      }
      uploadedPhotos.push(compressed);
      const url = URL.createObjectURL(compressed);
      const wrap = document.createElement('div');
      wrap.className = 'relative aspect-square rounded-lg overflow-hidden bg-[#F8F4ED]';
      wrap.innerHTML = `<img src="${url}" alt="" class="w-full h-full object-cover"><button type="button" class="absolute top-1 right-1 inline-flex items-center justify-center min-w-11 min-h-11 rounded-full bg-[#0A2540]/80 text-white text-base leading-none" aria-label="Verwijder foto">×</button>`;
      wrap.querySelector('button').addEventListener('click', () => {
        const idx = uploadedPhotos.indexOf(compressed);
        if (idx > -1) uploadedPhotos.splice(idx, 1);
        wrap.remove();
        photoStatus.textContent = `${uploadedPhotos.length} / ${MAX_PHOTOS} foto's`;
      });
      photoPreview.appendChild(wrap);
    }
    photoStatus.textContent = `${uploadedPhotos.length} / ${MAX_PHOTOS} foto's`;
    if (window.trackEvent) window.trackEvent('wizard_photo_uploaded', { photo_count: uploadedPhotos.length });
  });

  // ---- Review render ----
  function renderReview() {
    const data = new FormData(form);
    const dl = $('[data-wizard-review]');
    dl.innerHTML = '';
    const labels = {
      transport_choice: 'Transport',
      street: 'Adres', postcode: 'Postcode', city: 'Plaats', province: 'Provincie',
      name: 'Naam', email: 'E-mail', phone: 'Telefoon', phone_alt: 'Alt. telefoon',
      floor: 'Verdieping', parking_distance: 'Parkeer-afstand',
      elevator: 'Lift', narrow_street: 'Smalle straat',
      week_primary: '1e voorkeur leverweek', week_alt: 'Alternatief'
    };
    for (const [key, label] of Object.entries(labels)) {
      const val = data.get(key);
      if (val) {
        const row = document.createElement('div');
        row.className = 'flex justify-between gap-4 py-1';
        row.innerHTML = `<dt class="text-[#6E6457]">${label}</dt><dd class="text-[#0A2540] text-right">${val}</dd>`;
        dl.appendChild(row);
      }
    }
    if (uploadedPhotos.length) {
      const row = document.createElement('div');
      row.className = 'flex justify-between gap-4 py-1 pt-2 border-t border-[#0A2540]/10 mt-2';
      row.innerHTML = `<dt class="text-[#6E6457]">Foto's</dt><dd class="text-[#0A2540]">${uploadedPhotos.length} geüpload</dd>`;
      dl.appendChild(row);
    }
  }

  // ---- Nav buttons ----
  $('[data-wizard-next]').addEventListener('click', () => {
    if (!validateStep(currentStep)) return;
    if (currentStep < TOTAL_STEPS) showStep(stepAfter(currentStep, 1));
  });
  $('[data-wizard-prev]').addEventListener('click', () => {
    if (currentStep > 1) showStep(stepAfter(currentStep, -1));
  });

  // ---- Submit ----
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (!validateStep(TOTAL_STEPS)) return;
    const submitBtn = $('[data-wizard-submit]');
    submitBtn.disabled = true;
    submitBtn.textContent = 'Verzenden…';

    const data = new FormData(form);
    uploadedPhotos.forEach((p, i) => data.append(`photo_${i}`, p, p.name));

    // Configurator-state via query-string meenemen
    const qs = new URLSearchParams(window.location.search);
    qs.forEach((v, k) => data.append(`config_${k}`, v));

    const webhookUrl = document.querySelector('meta[name="trigger-webhook-url"]')?.content;
    try {
      // ---- 1. Altijd via Shopify's contactformulier, dat is de gegarandeerde route ----
      await verzendViaShopify(data);

      // ---- 2. Webhook alleen als extra, en een storing daar mag de aanvraag niet ----
      //         omgooien: de klant is al bij Brouwers binnen via stap 1.
      if (webhookUrl) {
        try {
          const r = await fetch(webhookUrl, { method: 'POST', body: data });
          if (!r.ok) console.warn('Webhook gaf ' + r.status + '; aanvraag is wel per mail verzonden.');
        } catch (whErr) {
          console.warn('Webhook onbereikbaar; aanvraag is wel per mail verzonden.', whErr);
        }
      }

      // ---- 3. Pas NA een geslaagde verzending meten en succes tonen ----
      if (window.trackEvent) {
        window.trackEvent('generate_lead', { currency: 'EUR', value: 50, lead_type: 'transport_quote' });
      }
      form.classList.add('hidden');
      $('[data-wizard-success]').classList.remove('hidden');
    } catch (err) {
      submitBtn.disabled = false;
      submitBtn.textContent = 'Verzend aanvraag';
      alert('Er ging iets mis bij het verzenden. Probeer het opnieuw of mail rechtstreeks naar info@boxspringsinspanje.nl.');
      console.error(err);
    }
  });

  // Zet de wizard-antwoorden om in een contactformulier-inzending. Gooit bij mislukking,
  // zodat de aanroeper geen succes toont en geen conversie meet.
  async function verzendViaShopify(data) {
    const leesbaar = {
      transport_choice: 'Transportkeuze',
      street: 'Straat', postcode: 'Postcode', city: 'Plaats', province: 'Provincie/regio',
      phone_alt: 'Tweede telefoonnummer',
      floor: 'Verdieping', elevator: 'Lift aanwezig', narrow_street: 'Smalle straat',
      parking_distance: 'Afstand parkeren', week_primary: 'Voorkeursweek',
      week_alt: 'Alternatieve week', agree_terms: 'Voorwaarden geaccepteerd',
    };

    const regels = [];
    for (const [k, v] of data.entries()) {
      if (v instanceof File) continue;                     // foto's kunnen hier niet mee
      if (['name', 'email', 'phone'].includes(k)) continue; // die gaan als eigen veld mee
      if (!String(v).trim()) continue;
      regels.push(`${leesbaar[k] || k}: ${v}`);
    }

    const aantalFotos = uploadedPhotos.length;
    if (aantalFotos > 0) {
      regels.push('');
      regels.push(`LET OP: de klant heeft ${aantalFotos} foto(s) toegevoegd. Die kunnen niet ` +
        'via het contactformulier mee. Vraag ze na per mail.');
    }

    const body = new FormData();
    body.append('form_type', 'contact');
    body.append('utf8', '✓');
    body.append('contact[name]', data.get('name') || '');
    body.append('contact[email]', data.get('email') || '');
    body.append('contact[phone]', data.get('phone') || '');
    body.append('contact[Soort aanvraag]', 'Transport offerte aanvragen (wizard)');
    body.append('contact[Aantal fotos]', String(aantalFotos));
    body.append('contact[body]', regels.join('\n'));

    const res = await fetch('/contact', { method: 'POST', body, redirect: 'follow' });
    if (!res.ok) throw new Error('Contactformulier gaf status ' + res.status);
  }

  // ---- localStorage resume ----
  const STORAGE_KEY = 'wizard-state-v1';
  const STORAGE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

  function saveStateToStorage() {
    if (!form) return;
    const data = {};
    form.querySelectorAll('input:not([type="file"]), select, textarea').forEach(el => {
      if (el.type === 'radio' && !el.checked) return;
      if (el.name && el.value) data[el.name] = el.type === 'checkbox' ? el.checked : el.value;
    });
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ data, step: currentStep, ts: Date.now() }));
    } catch (_) {}
  }

  function loadStateFromStorage() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed.ts || Date.now() - parsed.ts > STORAGE_TTL_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }
      Object.entries(parsed.data || {}).forEach(([name, val]) => {
        const el = form.querySelector(`[name="${name}"]`);
        if (!el || el.type === 'file') return;
        if (el.type === 'checkbox') el.checked = !!val;
        else if (el.type === 'radio') {
          const match = form.querySelector(`[name="${name}"][value="${CSS.escape(String(val))}"]`);
          if (match) match.checked = true;
        }
        else el.value = val;
      });
      applyTransportChoice();
      const resumeStep = Math.max(1, Math.min(TOTAL_STEPS, parsed.step || 1));
      if (resumeStep > 1) {
        const banner = document.createElement('div');
        banner.className = 'mb-6 p-4 rounded-xl bg-[#D4A017]/10 border border-[#D4A017]/30 text-[#0A2540] flex items-center justify-between gap-4';
        banner.innerHTML = `
          <span class="text-sm">Verder waar u was - uw eerdere stappen zijn bewaard.</span>
          <button type="button" class="text-sm underline" data-wizard-clear>Begin opnieuw</button>
        `;
        root.insertBefore(banner, root.firstChild);
        banner.querySelector('[data-wizard-clear]').addEventListener('click', () => {
          localStorage.removeItem(STORAGE_KEY);
          window.location.reload();
        });
        showStep(resumeStep);
      }
    } catch (_) {}
  }

  if (form) {
    form.addEventListener('input', () => saveStateToStorage());
    form.addEventListener('change', () => saveStateToStorage());
    form.addEventListener('submit', () => {
      try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
    });
  }

  // ---- Init ----
  showStep(1);
  // Transportkeuze vooraf gekozen in de configurator (?transport=zelf|bezorgen)
  const urlTransport = new URLSearchParams(window.location.search).get('transport');
  if (urlTransport === 'zelf') {
    const el = $('[data-transport-choice="zelf"]');
    if (el) el.checked = true;
  }
  applyTransportChoice();
  loadStateFromStorage();
  if (window.trackEvent) window.trackEvent('begin_checkout', { funnel_step: 'transport_wizard' });
})();
